import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { api } from './api';
import { errorMessage } from './errors';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

// "Summarize" in the header of a contact or deal page. `kind` is "contact" or
// "deal"; the backend only shows Audrey the fields on file and refuses a record
// the caller cannot open.
export default function AudreySummaryButton({ kind, id, className = '' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const { data } = await api.post('/v2/ai/summary', { kind, id });
      setResult(data);
    } catch (e) {
      // A spend-gate or permission denial is already toasted by the api layer.
      setError(errorMessage(e, 'Audrey could not write the summary. Try again in a minute.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={loading}
        className={className || 'inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-60'}
        data-testid="audrey-summary-btn"
      >
        <Sparkles size={14} /> Summarize
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="audrey-summary-dialog">
          <DialogHeader>
            <DialogTitle>Audrey&apos;s summary</DialogTitle>
          </DialogHeader>
          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6" data-testid="audrey-summary-loading">
              <Loader2 className="animate-spin" size={16} /> Reading the file...
            </div>
          )}
          {error && <div className="text-sm text-red-600" data-testid="audrey-summary-error">{error}</div>}
          {result && (
            <div className="space-y-3">
              <div className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="audrey-summary-text">{result.summary}</div>
              {result.grounded === false && (
                <div className="text-xs text-amber-700" data-testid="audrey-summary-ungrounded">
                  Some details could not be matched to the file, so Audrey left them out.
                </div>
              )}
              <div className="text-xs text-slate-500" data-testid="audrey-summary-note">
                AI-generated from what is on file. Review it before relying on it.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
